import React from "react";
import { Navigate, Route, Routes, useParams } from "react-router-dom";
import { Spin } from "antd";

import NotFound from "~/components/NotFound/NotFound";
import { usePostHogFeatureFlag } from "~/hooks/usePostHogFeatureFlag";
import AI from "~/modules/AI/AI";
import AppConfig from "~/modules/AppConfig/AppConfig";
import HelpCenterRouter from "~/modules/HelpCenter/HelpCenterRouter";
import { PermissionGuard } from "~/modules/Auth/PermissionGuard";
import AutomationRouter from "~/modules/Automation/AutomationRouter";
import BadgesRouter from "~/modules/Badges/BadgesRouter";
import { Campaigns } from "~/modules/Campaigns/Campaigns";
import ChallengesRouter from "~/modules/Challenges/ChallengesRouter";
import Dashboard from "~/modules/Dashboard/Dashboard";
import EngagementLayout from "~/modules/Engagement/EngagementLayout";
import HabitsRouter from "~/modules/Habits/HabitsRouter";
import MarketingAnalyticsRouter from "~/modules/MarketingAnalytics/MarketingAnalyticsRouter";
import NutritionRouter from "~/modules/Nutrition/NutritionRouter";
import ProductsRouter from "~/modules/Products/ProductsRouter";
import SalesRouter from "~/modules/Sales/SalesRouter";
import SupportRouter from "~/modules/Support/SupportRouter";
import SurveysRouter from "~/modules/SurveysTemplates/SurveysRouter";
import TeamRouter from "~/modules/Team/TeamRouter";
import TraineeRouter from "~/modules/Trainee/TraineeRouter";
import TrainerCalendar from "~/modules/TrainerCalendar/TrainerCalendar";
import TrainingProgramsRouter from "~/modules/TrainingPrograms/TrainingProgramsRouter";
import UserRouter from "~/modules/User/UserRouter";
import StudioRouter from "~/modules/Studio/StudioRouter";
import { SALES_CRM_FEATURE_FLAG } from "~/modules/Sales/salesFeatureFlag";
import { ADS_STUDIO_FEATURE_FLAG } from "~/modules/Studio/adsStudioFeatureFlag";
import { SUPPORT_BOARD_FEATURE_FLAG } from "~/modules/Support/supportFeatureFlag";
import Chat from "../Chat/Chat";
import { dashboardPermissionRequirements } from "./dashboardPermissionRequirements";
import Home from "./Home";
import LandingsRouter from "~/modules/Landings/LandingsRouter";

const SalesFeatureRoute = (): React.ReactElement => {
  const { enabled, isLoading } = usePostHogFeatureFlag(SALES_CRM_FEATURE_FLAG);

  if (isLoading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center">
        <Spin />
      </div>
    );
  }

  return enabled ? <SalesRouter /> : <NotFound />;
};

const SupportFeatureRoute = (): React.ReactElement => {
  const { enabled, isLoading } = usePostHogFeatureFlag(SUPPORT_BOARD_FEATURE_FLAG);

  if (isLoading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center">
        <Spin />
      </div>
    );
  }

  return enabled ? <SupportRouter /> : <NotFound />;
};

const StudioFeatureRoute = (): React.ReactElement => {
  const { enabled, isLoading } = usePostHogFeatureFlag(ADS_STUDIO_FEATURE_FLAG);

  if (isLoading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center">
        <Spin />
      </div>
    );
  }

  return enabled ? <StudioRouter /> : <NotFound />;
};

/** Stare linki do karty sprawy (`/collections/:caseId`) prowadzą teraz pod Produkty. */
const CollectionsLegacyRedirect = () => {
  const { caseId } = useParams<{ caseId: string }>();
  return <Navigate to={`/products/collections/${caseId}`} replace />;
};

const DashboardRouter = (): React.ReactElement => {
  return (
    <Routes>
      <Route element={<Dashboard />}>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="/dashboard/*" element={<Home />} />
        <Route
          path="/trainee/*"
          element={
            <PermissionGuard requirement={dashboardPermissionRequirements.traineeList}>
              <TraineeRouter />
            </PermissionGuard>
          }
        />
        <Route
          path="/calendar"
          element={
            <PermissionGuard requirement={dashboardPermissionRequirements.calendar}>
              <TrainerCalendar />
            </PermissionGuard>
          }
        />
        {/* Challenges / habits / badges share one "Zaangażowanie" shell (tabs) + one flag guard,
            but keep their top-level URLs so existing links don't break. */}
        <Route
          element={
            <PermissionGuard requirement={dashboardPermissionRequirements.engagement}>
              <EngagementLayout />
            </PermissionGuard>
          }
        >
          <Route path="/challenges/*" element={<ChallengesRouter />} />
          <Route path="/habits/*" element={<HabitsRouter />} />
          <Route path="/badges/*" element={<BadgesRouter />} />
        </Route>
        <Route
          path="/products/*"
          element={
            <PermissionGuard requirement={dashboardPermissionRequirements.products}>
              <ProductsRouter />
            </PermissionGuard>
          }
        />
        <Route
          path="/sales/*"
          element={
            <PermissionGuard requirement={dashboardPermissionRequirements.sales}>
              <SalesFeatureRoute />
            </PermissionGuard>
          }
        />
        <Route
          path="/support/*"
          element={
            <PermissionGuard requirement={dashboardPermissionRequirements.support}>
              <SupportFeatureRoute />
            </PermissionGuard>
          }
        />
        <Route
          path="/marketing/*"
          element={
            <PermissionGuard requirement={dashboardPermissionRequirements.marketing}>
              <MarketingAnalyticsRouter />
            </PermissionGuard>
          }
        />
        <Route
          path="/studio/*"
          element={
            <PermissionGuard requirement={dashboardPermissionRequirements.studio}>
              <StudioFeatureRoute />
            </PermissionGuard>
          }
        />
        <Route
          path="/programs/*"
          element={
            <PermissionGuard requirement={dashboardPermissionRequirements.programs}>
              <TrainingProgramsRouter />
            </PermissionGuard>
          }
        />
        <Route
          path="/surveys/*"
          element={
            <PermissionGuard requirement={dashboardPermissionRequirements.surveys}>
              <SurveysRouter />
            </PermissionGuard>
          }
        />
        <Route
          path="/campaigns/*"
          element={
            <PermissionGuard requirement={dashboardPermissionRequirements.campaigns}>
              <Campaigns />
            </PermissionGuard>
          }
        />
        <Route
          path="/landings/*"
          element={
            <PermissionGuard requirement={dashboardPermissionRequirements.landings}>
              <LandingsRouter />
            </PermissionGuard>
          }
        />
        <Route
          path="/team/*"
          element={
            <PermissionGuard requirement={dashboardPermissionRequirements.teamArea}>
              <TeamRouter />
            </PermissionGuard>
          }
        />
        <Route path="/user/*" element={<UserRouter />} />
        <Route
          path="/nutrition/*"
          element={
            <PermissionGuard requirement={dashboardPermissionRequirements.nutrition}>
              <NutritionRouter />
            </PermissionGuard>
          }
        />
        <Route
          path="/automation/*"
          element={
            <PermissionGuard requirement={dashboardPermissionRequirements.automation}>
              <AutomationRouter />
            </PermissionGuard>
          }
        />
        <Route
          path="/ai/*"
          element={
            <PermissionGuard requirement={dashboardPermissionRequirements.ai}>
              <AI />
            </PermissionGuard>
          }
        />
        <Route
          path="/app-config/*"
          element={
            <PermissionGuard requirement={dashboardPermissionRequirements.appConfig}>
              <AppConfig />
            </PermissionGuard>
          }
        />
        {/* Należności są zakładką w Produktach; stare linki do /collections przekierowujemy. */}
        <Route path="/collections" element={<Navigate to="/products?tab=collections" replace />} />
        <Route path="/collections/:caseId" element={<CollectionsLegacyRedirect />} />
        <Route
          path="/help-center/*"
          element={
            <PermissionGuard requirement={dashboardPermissionRequirements.helpCenter}>
              <HelpCenterRouter />
            </PermissionGuard>
          }
        />
        <Route
          path="/chat/*"
          element={
            <PermissionGuard requirement={dashboardPermissionRequirements.chat}>
              <Chat />
            </PermissionGuard>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
};

export default DashboardRouter;
